import { RadioGroup } from '@headlessui/react';
import { CheckIcon } from '@heroicons/react/24/outline';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../components/forms/Buttons/Button';
import Input from '../../components/forms/Input/Input';
import Upload from '../../components/Upload/Upload';
import {
  clearStepDraft,
  readStepDraft,
  writeStepDraft,
} from '../../utils/helpers/registrationDraft';

export default function SpecialNeeds({
  applicant,
  setBody,
  nextPage,
  setSpecialNeedsPartCompleted,
}) {
  const [t] = useTranslation();
  const [hasSpecialNeeds, setHasSpecialNeeds] = useState(
    applicant?.is_inclusive ? 'yes' : 'no',
  );
  const [inclusiveComments, setInclusiveComments] = useState(
    applicant?.inclusive_comments || '',
  );
  const [selectedConditions, setSelectedConditions] = useState(
    applicant?.inclusiveness || [],
  );
  const [selectedRequirements, setSelectedRequirements] = useState(
    applicant?.inclusive_needs || [],
  );
  // тексты «Другое» попадают в body только по «Сохранить»
  const [otherCondition, setOtherCondition] = useState(
    () => readStepDraft('special')?.otherCondition ?? '',
  );
  const [otherRequirement, setOtherRequirement] = useState(
    () => readStepDraft('special')?.otherRequirement ?? '',
  );
  const [inclusivenessDocument, setInclusivenessDocument] = useState(
    applicant?.inclusiveness_document || null,
  );

  const yesNoOptions = [
    {
      name: t('signup.yes'),
      value: 'yes',
    },
    {
      name: t('signup.no'),
      value: 'no',
    },
  ];

  const conditionOptions = [
    { id: 'VISION', label: t('signup.special_needs.vision') },
    { id: 'HEARING', label: t('signup.special_needs.hearing') },
    { id: 'MOBILITY', label: t('signup.special_needs.mobility') },
    { id: 'OTHER', label: t('buttons.other') },
  ];

  const requirementOptions = [
    {
      id: 'ACCESSIBLE',
      label: t('signup.special_needs.accessible_environment'),
    },
    { id: 'LARGE_FONT', label: t('signup.special_needs.large_font') },
    { id: 'BRAILLE', label: t('signup.special_needs.braille') },
    { id: 'OTHER', label: t('buttons.other') },
  ];

  useEffect(() => {
    if (hasSpecialNeeds === 'no') {
      setBody((body) => ({
        ...body,
        is_inclusive: false,
        inclusive_comments: null,
        inclusiveness: [],
        inclusive_needs: [],
        inclusiveness_document: null,
      }));
      setInclusiveComments('');
      setSelectedConditions([]);
      setSelectedRequirements([]);
      setOtherCondition('');
      setOtherRequirement('');
      setInclusivenessDocument(null);
    } else {
      setBody((body) => ({
        ...body,
        is_inclusive: true,
      }));
    }

    return () => {};
  }, [hasSpecialNeeds, setBody]);

  useEffect(() => {
    writeStepDraft('special', { otherCondition, otherRequirement });
  }, [otherCondition, otherRequirement]);

  const handleConditionToggle = (conditionId) => {
    setSelectedConditions((prev) => {
      const newConditions = prev.includes(conditionId)
        ? prev.filter((id) => id !== conditionId)
        : [...prev, conditionId];

      setBody((body) => ({
        ...body,
        inclusiveness: newConditions,
      }));

      return newConditions;
    });
  };

  const handleRequirementToggle = (requirementId) => {
    setSelectedRequirements((prev) => {
      const newRequirements = prev.includes(requirementId)
        ? prev.filter((id) => id !== requirementId)
        : [...prev, requirementId];

      setBody((body) => ({
        ...body,
        inclusive_needs: newRequirements,
      }));

      return newRequirements;
    });
  };

  const handleSave = () => {
    let finalComments = inclusiveComments;
    let finalConditions = otherCondition && selectedConditions.includes('OTHER') ? [...selectedConditions, otherCondition] : selectedConditions;
    let finalRequirements = otherRequirement && selectedRequirements.includes('OTHER') ? [...selectedRequirements, otherRequirement] : selectedRequirements;
    setBody((body) => ({
      ...body,
      is_inclusive: hasSpecialNeeds === 'yes',
      inclusive_comments: finalComments,
      inclusiveness: finalConditions.filter((item) => item !== 'OTHER'),
      inclusive_needs: finalRequirements.filter((item) => item !== 'OTHER'),
      inclusiveness_document: inclusivenessDocument,
    }));
    // тексты уже в body; при возврате на шаг поля «Другое» снова пустые
    clearStepDraft('special');
    setSpecialNeedsPartCompleted(true);
    nextPage(6);
  };

  const canProceed = () => {
    if (hasSpecialNeeds === 'no') {
      return true;
    }
    if (
      hasSpecialNeeds === 'yes' &&
      inclusiveComments &&
      selectedConditions.length > 0 &&
      selectedRequirements.length > 0
    ) {
      return true;
    }
    return false;
  };

  return (
    <div className='flex flex-col gap-4'>
      <div>
        <h3 className='mb-2 text-base font-semibold italic'>
          {t('signup.special_needs.question_1')} 
        </h3>
        <RadioGroup value={hasSpecialNeeds} onChange={setHasSpecialNeeds}>
          <RadioGroup.Label className='sr-only'>
            Special needs question
            
          </RadioGroup.Label>
          <div className='space-y-2'>
            {yesNoOptions.map((option) => (
              <RadioGroup.Option
                key={option.name}
                value={option?.value}
                className={({ active, checked }) =>
                  `${
                    active
                      ? 'ring-2 ring-white ring-opacity-60 ring-offset-2 ring-offset-sky-300'
                      : ''
                  }
                  ${
                    checked ? 'bg-sky-900 bg-opacity-75 text-white' : 'bg-white'
                  }
                    relative flex cursor-pointer rounded-lg py-2 px-4  shadow-md focus:outline-none`
                }
              >
                {({ active, checked }) => (
                  <>
                    <div className='flex w-full items-center justify-between'>
                      <div className='flex  items-center'>
                        <div className='text-sm'>
                          <RadioGroup.Label
                            as='p'
                            className={`font-medium  ${
                              checked ? 'text-white' : 'text-gray-900'
                            }`}
                          >
                            {option?.name}
                          </RadioGroup.Label>
                        </div>
                      </div>
                      {checked && (
                        <div className='shrink-0 text-white'>
                          <CheckIcon className='h-6 w-6' />
                        </div>
                      )}
                    </div>
                  </>
                )}
              </RadioGroup.Option>
            ))}
          </div>
        </RadioGroup>
      </div>

      {hasSpecialNeeds === 'yes' && (
        <>
          <div>
            <Input
              label={t('signup.special_needs.please_specify')}
              value={inclusiveComments}
              required
              onChange={(e) => {
                setInclusiveComments(e.target.value);
                setBody((body) => ({
                  ...body,
                  inclusive_comments: e.target.value,
                }));
              }}
              placeholder={t('signup.special_needs.details_placeholder')}
            />
          </div>

          <div>
            <h3 className='mb-2 text-base font-semibold italic'>
              {t('signup.special_needs.question_2')}
              <span className='text-red-600'> *</span>
            </h3>
            <p className='mb-2 text-sm text-gray-600'>
              {t('signup.special_needs.multiple_choice')}
            </p>
            <div className='space-y-2'>
              {conditionOptions.map((condition) => (
                <div key={condition.id} className='flex items-center'>
                  <input
                    type='checkbox'
                    id={`condition-${condition.id}`}
                    checked={selectedConditions.includes(condition.id)}
                    onChange={() => handleConditionToggle(condition.id)}
                    className='h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500'
                  />
                  <label
                    htmlFor={`condition-${condition.id}`}
                    className='ml-2 block text-sm text-gray-900'
                  >
                    {condition.label}
                  </label>
                </div>
              ))}
            </div>

            {selectedConditions.includes('OTHER') && (
              <div className='mt-2'>
                <Input
                  value={otherCondition}
                  onChange={(e) => {
                    setOtherCondition(e.target.value);
                  }}
                  placeholder={t('signup.special_needs.specify_other')}
                />
              </div>
            )}
          </div>

          <div>
            <h3 className='mb-2 text-base font-semibold italic'>
              {t('signup.special_needs.question_3')}
              <span className='text-red-600'> *</span>
            </h3>
            <p className='mb-2 text-sm text-gray-600'>
              {t('signup.special_needs.multiple_choice')}
            </p>
            <div className='space-y-2'>
              {requirementOptions.map((requirement) => (
                <div key={requirement.id} className='flex items-center'>
                  <input
                    type='checkbox'
                    id={`requirement-${requirement.id}`}
                    checked={selectedRequirements.includes(requirement.id)}
                    onChange={() => handleRequirementToggle(requirement.id)}
                    className='h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500'
                  />
                  <label
                    htmlFor={`requirement-${requirement.id}`}
                    className='ml-2 block text-sm text-gray-900'
                  >
                    {requirement.label}
                  </label>
                </div>
              ))}
            </div>

            {selectedRequirements.includes('OTHER') && (
              <div className='mt-2'>
                <Input
                  value={otherRequirement}
                  onChange={(e) => {
                    setOtherRequirement(e.target.value);
                  }}
                  placeholder={t('signup.special_needs.specify_other')}
                />
              </div>
            )}
          </div>

          <div>
            <h3 className='mb-2 text-base font-semibold'>
              {t('signup.special_needs.upload_document')}
            </h3>
            <p className='mb-2 text-sm text-gray-600'>
              {t('signup.special_needs.document_optional')}
            </p>
            <Upload
              accept='image/jpeg,image/gif,image/png,application/pdf,image/jpg'
              file={inclusivenessDocument}
              setFile={(file) => {
                setInclusivenessDocument(file);
                setBody((body) => ({
                  ...body,
                  inclusiveness_document: file,
                }));
              }}
              desc={t('signup.special_needs.upload_document')}
              descRight={t('process')}
            />
          </div>
        </>
      )}

      <div className='mt-6'>
        <div className='flex flex-row justify-end'>
          <Button
            type='submit'
            text={t('buttons.save')}
            onClick={handleSave}
            disabled={!canProceed()}
          />
        </div>
      </div>
    </div>
  );
}

